import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { GOOGLE_CALENDAR_CONFIG } from '@/lib/calendar/config';
import { captureError } from '@/lib/sentry';

/**
 * Handles OAuth callbacks for calendar connections.
 * - Google: exchanges auth code for tokens, upserts email_providers, fetches calendar list
 * - Outlook: uses existing outlookService MSAL redirect, fetches calendar list
 *
 * Both providers reuse email_providers — same tokens work for email + calendar.
 */
export function CalendarCallback() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const hasHandled = useRef(false);
  const [status, setStatus] = useState('Connecting your calendar...');

  useEffect(() => {
    if (hasHandled.current) return;
    hasHandled.current = true;

    const handleCallback = async () => {
      try {
        const provider = localStorage.getItem('connecting_provider');
        const workspaceId = localStorage.getItem('connecting_workspace_id');

        if (!workspaceId) throw new Error('No workspace ID found');
        if (!provider) throw new Error('No provider type found');

        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');

        if (provider === 'google_calendar') {
          await handleGoogleCalendarCallback(workspaceId, user.id, setStatus);
        } else {
          throw new Error(`Unknown provider: ${provider}`);
        }

        localStorage.removeItem('connecting_workspace_id');
        localStorage.removeItem('connecting_provider');

        toast({ description: "Calendar connected successfully" });
        navigate('/settings/calendar');
      } catch (error) {
        console.error('[CalendarCallback] Error:', error);
        setStatus('Connection failed');
        captureError(error as Error, { feature: 'calendar-oauth-callback' });
        toast({
          title: "Error",
          description: error instanceof Error ? error.message : "Failed to connect calendar",
          variant: "destructive",
        });
        setTimeout(() => navigate('/settings/calendar'), 2000);
      }
    };

    handleCallback();
  }, [navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground">{status}</p>
      </div>
    </div>
  );
}

async function handleGoogleCalendarCallback(
  workspaceId: string,
  userId: string,
  setStatus: (s: string) => void,
) {
  const code = new URLSearchParams(window.location.search).get('code');
  if (!code) throw new Error('No authorization code found');

  setStatus('Exchanging authorization code...');

  // Exchange code for tokens (with email+calendar scopes)
  const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      code,
      client_id: GOOGLE_CALENDAR_CONFIG.clientId,
      client_secret: GOOGLE_CALENDAR_CONFIG.clientSecret,
      redirect_uri: GOOGLE_CALENDAR_CONFIG.redirectUri,
      grant_type: 'authorization_code',
    }),
  });

  if (!tokenResponse.ok) {
    const errorData = await tokenResponse.json();
    throw new Error(errorData.error_description || 'Failed to exchange code');
  }

  const tokens = await tokenResponse.json();

  setStatus('Getting account info...');

  // Get user email
  const userResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
    headers: { 'Authorization': `Bearer ${tokens.access_token}` },
  });
  if (!userResponse.ok) throw new Error('Failed to get user info');
  const userData = await userResponse.json();

  setStatus('Saving connection...');

  // Step 1: upsert provider metadata WITHOUT tokens (clients cannot write
  // to the encrypted bytea columns).
  const { data: provider, error: dbError } = await supabase
    .from('email_providers')
    .upsert({
      type: 'gmail',
      email: userData.email,
      workspace_id: workspaceId,
      user_id: userId,
      display_name: userData.name,
      expires_at: new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
      updated_at: new Date().toISOString(),
    }, {
      onConflict: 'workspace_id,type,email',
    })
    .select()
    .single();

  if (dbError) throw dbError;

  // Step 2: store tokens via SECURITY DEFINER RPC (encrypts).
  const { error: tokError } = await supabase.rpc('email_provider_set_tokens', {
    p_provider_id:   provider.id,
    p_access_token:  tokens.access_token,
    p_refresh_token: tokens.refresh_token,
    p_expires_at:    new Date(Date.now() + tokens.expires_in * 1000).toISOString(),
  });
  if (tokError) {
    console.error('[CalendarCallback] email_provider_set_tokens failed:', tokError);
    throw tokError;
  }

  setStatus('Fetching your calendars...');

  // Fetch Google calendar list
  await fetchGoogleCalendarList(tokens.access_token, provider.id, workspaceId, userId);
}

async function fetchGoogleCalendarList(
  accessToken: string,
  providerId: string,
  workspaceId: string,
  userId: string,
) {
  const response = await fetch(
    'https://www.googleapis.com/calendar/v3/users/me/calendarList',
    { headers: { 'Authorization': `Bearer ${accessToken}` } },
  );
  if (!response.ok) {
    console.error('Failed to fetch Google calendar list:', await response.text());
    return;
  }

  const data = await response.json();
  const calendars = (data.items || []).map((cal: any) => ({
    provider_id: providerId,
    workspace_id: workspaceId,
    user_id: userId,
    external_id: cal.id,
    name: cal.summary || cal.id,
    color: cal.backgroundColor || null,
    is_primary: cal.primary || false,
    is_selected: cal.primary || false,
    access_role: cal.accessRole || 'reader',
  }));

  if (calendars.length > 0) {
    const { error } = await supabase
      .from('calendar_calendars')
      .upsert(calendars, { onConflict: 'provider_id,external_id' });
    if (error) console.error('Error saving Google calendars:', error);
  }
}
